import React, { Component } from "react";
import { connect } from "react-redux";
import {
  withStyles,
  Grid,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Snackbar,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { ArrowBackOutlined } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import axios from "axios";
import { URL } from "../../config/config";
import { SnackbarContext } from "../../commons/Snackbar/Snackbar";
import { isGlobalAdmin } from "../../helpers/accessControlHelper";
import ClientAccountTeam from "./ClientAccountTeam";

class Clients extends Component {
  static contextType = SnackbarContext;
  constructor(props) {
    super(props);
    const { agents, agentIndex } = this.props;
    this.state = {
      alert: false,
      alertMsg: "",
      alertSeverity: "",
      copySuccess: false,
      copiedAssistantId: null,
      email: agents[agentIndex].email,
      getOtpResponseData: [],
      // Prefilled with a `+` so the operator always sees, and edits, a full
      // international number — a national-only value is what breaks OTP delivery.
      phoneInput: agents[agentIndex].contact
        ? `+${String(agents[agentIndex].contact).replace(/^\+/, "")}`
        : "",
      twoFAInput: agents[agentIndex].specialAccess?.enable2FAAuth?.twoFANumber
        ? `+${String(
            agents[agentIndex].specialAccess.enable2FAAuth.twoFANumber
          ).replace(/^\+/, "")}`
        : "",
      setTwoFAFromContact: false,
      phonePreview: null,
      phonePreviewError: "",
      phoneUpdateInProgress: false,
      // null until loaded, so the switch stays disabled rather than showing
      // a wrong "off" state.
      automationAccount: null,
      automationAccountUpdating: false,
    };
  }

  getOTPHandler = async (event) => {
    const { agentIndex, agents } = this.props;
    const agent = agents[agentIndex];
    const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

    const email = isValidEmail(agent.name) ? agent.name : agent.email;

    const payload = {
      email,
      events: ["login", "passwordReset", "platform"],
      name: agent.name,
    };

    try {
      const response = await axios.post(`${URL}/superadmin/otp`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data) {
        this.setState({
          getOtpResponseData: response.data.results,
        });
      }
    } catch (error) {
      console.error("Error:", error.response?.data || error.message);
    }
  };

  componentDidMount = () => {
    this.getOTPHandler();
    if (this.canManageAutomationAccount()) this.getAutomationAccount();
  };

  // The flag lives on the owner (clientId === _id) and only global admins can
  // read or change it (PRO-190).
  canManageAutomationAccount = () => {
    const agent = this.props.agents[this.props.agentIndex];
    return (
      isGlobalAdmin(this.props.agent?.permissions) &&
      !!agent?._id &&
      String(agent._id) === String(agent.clientId)
    );
  };

  getAutomationAccount = async () => {
    const agent = this.props.agents[this.props.agentIndex];
    try {
      const { data } = await axios.get(
        `${URL}/superadmin/automation-account/${agent._id}`
      );
      this.setState({ automationAccount: !!data?.enabled });
    } catch (error) {
      console.error("Error:", error.response?.data || error.message);
    }
  };

  toggleAutomationAccount = async (enabled) => {
    const { showSnackbar } = this.context;
    const agent = this.props.agents[this.props.agentIndex];
    const name = agent.name || agent.email;

    const confirmed = window.confirm(
      enabled
        ? `Mark ${name} as an automation account?\n\n` +
            "The master OTP will log in this account's owner, managers and agents on every environment, including production, and 2FA OTPs will no longer be sent."
        : `Remove automation access from ${name}?\n\nLogins will need a real 2FA OTP again.`
    );
    if (!confirmed) return;

    this.setState({ automationAccountUpdating: true });
    try {
      const { data } = await axios.post(
        `${URL}/superadmin/automation-account`,
        { clientId: agent._id, enabled }
      );
      this.setState({ automationAccount: !!data?.enabled });
      showSnackbar(
        enabled ? "Automation access granted." : "Automation access revoked.",
        "success"
      );
    } catch (error) {
      showSnackbar(
        error.response?.data?.message ||
          error.response?.data?.error?.message ||
          "Could not update automation access",
        "error"
      );
    } finally {
      this.setState({ automationAccountUpdating: false });
    }
  };
  handleInput = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  closeSnackbar = () => {
    this.setState({ alert: false, alertMsg: "", alertSeverity: "" });
  };

  resetButtonHandler = async (event) => {
    const { showSnackbar } = this.context;
    const { agentIndex, agents } = this.props;
    const agent = agents[agentIndex];
    const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

    const email = event === "login" ? agent.name : isValidEmail(agent.name) ? agent.name : agent.email;

    const payload = {
      email,
      events: [event],
    };

    try {
      await axios.post(`${URL}/superadmin/otp/reset`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      showSnackbar("OTP limit reset successfully.", "success");
    } catch (error) {
      console.error("Error:", error.response?.data || error.message);
    }
  };

  updateAgentDetails = async (data) => {
    try {
      const agent = this.props.agents[this.props.agentIndex];
      await axios.patch(`${URL}/superadmin/update-agent-details`, {
        previousEmail: agent.email,
        agentId: agent._id,
        ...data,
      });
      this.setState({
        alert: true,
        alertMsg: "Updated successfully!",
        alertSeverity: "success",
      });
      this.props.setAgent({ ...agent, ...data });
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.messsage ||
        "Something went wrong. Cannot migrate this Partner project to AiSensy!";
      this.setState({
        alert: true,
        alertMsg: message,
        alertSeverity: "error",
      });
    }
  };

  // The preview is a server dry-run rather than a local derivation: the frontend's
  // config/dialCodes.js and the backend's countryNameMap disagree on one country
  // name, so only the server can say exactly what will be stored.
  refreshPhonePreview = async () => {
    const { phoneInput } = this.state;
    if (!phoneInput?.trim()) {
      this.setState({ phonePreview: null, phonePreviewError: "" });
      return;
    }

    try {
      const agent = this.props.agents[this.props.agentIndex];
      const { data } = await axios.patch(
        `${URL}/superadmin/update-agent-phone`,
        { agentId: agent._id, contact: phoneInput, dryRun: true }
      );
      this.setState({
        phonePreview: data?.preview?.applied || null,
        phonePreviewError: "",
      });
    } catch (error) {
      this.setState({
        phonePreview: null,
        phonePreviewError:
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          "Could not read that number",
      });
    }
  };

  updateAgentPhone = async ({ contact, twoFANumber, setTwoFAFromContact }) => {
    const agent = this.props.agents[this.props.agentIndex];
    const changesTwoFA = !!twoFANumber || !!setTwoFAFromContact;

    const summary = [
      contact ? `Phone: ${agent.contact || "(none)"} -> ${contact}` : null,
      changesTwoFA
        ? `2FA number: ${
            agent.specialAccess?.enable2FAAuth?.twoFANumber || "(none)"
          } -> ${setTwoFAFromContact ? contact : twoFANumber}`
        : null,
    ]
      .filter(Boolean)
      .join("\n");

    if (
      !window.confirm(
        `Update ${agent.name || agent.email}?\n\n${summary}\n\n` +
          (changesTwoFA
            ? "Changing the 2FA number logs this account out of all active sessions."
            : "Country, country code and ISO fields are updated to match.")
      )
    ) {
      return;
    }

    this.setState({ phoneUpdateInProgress: true });
    try {
      const { data } = await axios.patch(
        `${URL}/superadmin/update-agent-phone`,
        {
          agentId: agent._id,
          ...(contact ? { contact } : {}),
          ...(twoFANumber ? { twoFANumber } : {}),
          ...(setTwoFAFromContact ? { setTwoFAFromContact: true } : {}),
        }
      );

      const applied = data?.updated || {};
      const nextAgent = { ...agent };
      if (applied.contact) {
        nextAgent.contact = applied.contact;
        nextAgent.countryCode = applied.countryCode;
        nextAgent.country = applied.country;
      }
      if (applied.twoFANumber) {
        nextAgent.specialAccess = {
          ...(agent.specialAccess || {}),
          enable2FAAuth: {
            ...(agent.specialAccess?.enable2FAAuth || {}),
            twoFANumber: applied.twoFANumber,
          },
        };
      }
      this.props.setAgent(nextAgent);

      this.setState({
        alert: true,
        alertMsg: data?.sessionsTerminated
          ? "Updated. All sessions for this account were terminated."
          : "Updated successfully!",
        alertSeverity: "success",
        setTwoFAFromContact: false,
        ...(applied.contact ? { phoneInput: `+${applied.contact}` } : {}),
        ...(applied.twoFANumber
          ? { twoFAInput: `+${applied.twoFANumber}` }
          : {}),
      });
    } catch (error) {
      this.setState({
        alert: true,
        alertMsg:
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          "Something went wrong. Could not update the number.",
        alertSeverity: "error",
      });
    } finally {
      this.setState({ phoneUpdateInProgress: false });
    }
  };

  getAccountType = () => {
    const { agent } = this.props;
    if (agent.googleId && agent.password) return "Hybrid(Google & Password)";
    if (agent.googleId) return "Google Account";
    if (agent.appleId) return "Apple ID";
    return "Normal Account";
  };

  handleCopy = () => {
    const { agentIndex, agents } = this.props;
    const agent = agents[agentIndex];
    navigator.clipboard.writeText(agent.name);
    this.setState({ copySuccess: true });

    setTimeout(() => {
      this.setState({ copySuccess: false });
    }, 2000);
  };

  handleCopyAssistantId = (assistantId) => {
    navigator.clipboard.writeText(assistantId);
    this.setState({ copiedAssistantId: assistantId });

    setTimeout(() => {
      this.setState({ copiedAssistantId: null });
    }, 2000);
  };
  render() {
    const { classes, toggleDialog, agentIndex, agents, permissions } =
      this.props;
    const agent = agents[agentIndex];

    const clientsPermission = permissions.find((p) => p.label === "clients");
    const clientsWritePermission = clientsPermission?.write;

    const { copySuccess } = this.state;
    return (
      <Box px={2} className={classes.dialogContainer}>
        <Grid container alignItems="center" className={classes.dialogTopbar}>
          <Grid item>
            <IconButton onClick={toggleDialog}>
              <ArrowBackOutlined />
            </IconButton>
          </Grid>
          <Grid item>{agent.displayName}</Grid>
        </Grid>
        <div className={classes.root}>
          <Grid container justify="center">
            <Grid item xs={12} md={8}>
              <Box py={2} px={2} my={2}>
                <Grid
                  container
                  justify="space-between"
                  alignItems="center"
                ></Grid>
              </Box>
              <Box py={2} px={2} my={2} className={classes.container}>
                <Typography
                  style={{
                    fontSize: "18px",
                    marginBottom: "24px",
                  }}
                  variant="body1"
                  gutterBottom
                >
                  <strong>Account Details:</strong>
                </Typography>

                <Typography variant="body1" gutterBottom>
                  <strong
                    style={{
                      fontSize: "16px",
                    }}
                  >
                    Account Type:
                  </strong>{" "}
                  {this.getAccountType()}
                </Typography>

                <Typography variant="body1" gutterBottom>
                  <strong
                    style={{
                      fontSize: "16px",
                    }}
                  >
                    Email:{" "}
                  </strong>
                  {agent.email}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong
                    style={{
                      fontSize: "16px",
                    }}
                  >
                    Phone:{" "}
                  </strong>
                  {agent.contact}
                </Typography>
                <Box my={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        style={{
                          width: "100%",
                          border: "1px solid ",
                          background: "white",
                          marginRight: 10,
                        }}
                        name="email"
                        onChange={this.handleInput}
                        value={this.state.email}
                      />
                    </Grid>
                    <Grid item>
                      {!clientsWritePermission ? (
                        <></>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            this.updateAgentDetails({ email: this.state.email })
                          }
                        >
                          Update Email
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </div>

        {/* PRO-262: Account Team for direct clients; team members see it read-only */}
        {!agent.partnerId && (
          <div className={classes.root}>
            <Grid container justify="center">
              <Grid item xs={12} md={8}>
                <ClientAccountTeam
                  clientId={String(agent.clientId || agent._id)}
                  canWrite={
                    !!clientsWritePermission &&
                    String(agent._id) === String(agent.clientId)
                  }
                  containerClass={classes.container}
                />
              </Grid>
            </Grid>
          </div>
        )}

        <div className={classes.root}>
          <Grid container justify="center">
            <Grid item xs={12} md={8}>
              <Box py={2} px={2} my={2} className={classes.container}>
                <Typography
                  variant="body1"
                  gutterBottom
                  style={{
                    fontWeight: 900,
                    fontSize: "18px",
                    marginBottom: "24px",
                  }}
                >
                  Additional details
                </Typography>
                <Box
                  style={{
                    padding: "12px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                    width: "fit-content",
                  }}
                >
                  <Typography
                    style={{
                      fontSize: "16px",
                      fontWeight: "500",
                      color: "gray",
                    }}
                  >
                    Login Email/User Name:
                  </Typography>
                  <Typography style={{ fontSize: "16px", fontWeight: "600" }}>
                    {agent.name}
                  </Typography>
                  <Tooltip title={copySuccess ? "Copied!" : "Copy"}>
                    <IconButton
                      size="small"
                      onClick={this.handleCopy}
                      style={{
                        backgroundColor: "#f0f0f0",
                        borderRadius: "6px",
                        padding: "5px",
                        transition: "background 0.2s",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.background = "#e0e0e0")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.background = "#f0f0f0")
                      }
                    >
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </div>

        {isGlobalAdmin(this.props.agent?.permissions) &&
          clientsWritePermission && (
            <div className={classes.root}>
              <Grid container justify="center">
                <Grid item xs={12} md={8}>
                  <Box py={2} px={2} my={2} className={classes.container}>
                    <Typography
                      variant="body1"
                      gutterBottom
                      style={{
                        fontWeight: 900,
                        fontSize: "18px",
                        marginBottom: "24px",
                      }}
                    >
                      Phone &amp; 2FA
                    </Typography>

                    <Typography variant="body2" gutterBottom>
                      Current: contact <strong>{agent.contact || "—"}</strong> ·
                      country code{" "}
                      <strong>{agent.countryCode || "—"}</strong> · country{" "}
                      <strong>{agent.country || "—"}</strong>
                    </Typography>
                    <Typography
                      variant="body2"
                      gutterBottom
                      style={{ marginBottom: "16px" }}
                    >
                      2FA:{" "}
                      <strong>
                        {agent.specialAccess?.enable2FAAuth?.value
                          ? "enabled"
                          : "disabled"}
                      </strong>{" "}
                      · number{" "}
                      <strong>
                        {agent.specialAccess?.enable2FAAuth?.twoFANumber || "—"}
                      </strong>
                    </Typography>

                    <Box
                      style={{
                        padding: "16px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        marginBottom: "16px",
                      }}
                    >
                      <Typography
                        variant="body2"
                        gutterBottom
                        style={{ fontWeight: 600 }}
                      >
                        Phone number (with country code)
                      </Typography>
                      <TextField
                        style={{
                          width: "100%",
                          border: "1px solid ",
                          background: "white",
                        }}
                        name="phoneInput"
                        placeholder="+971 52 516 8261"
                        value={this.state.phoneInput}
                        onChange={this.handleInput}
                        onBlur={this.refreshPhonePreview}
                      />

                      {this.state.phonePreviewError ? (
                        <Typography
                          variant="body2"
                          style={{ color: "#d32f2f", marginTop: "8px" }}
                        >
                          {this.state.phonePreviewError}
                        </Typography>
                      ) : null}

                      {this.state.phonePreview ? (
                        <Box style={{ marginTop: "8px" }}>
                          <Typography variant="body2" style={{ color: "gray" }}>
                            Will write:
                          </Typography>
                          <Typography variant="body2">
                            contact{" "}
                            <strong>{this.state.phonePreview.contact}</strong> ·
                            countryCode{" "}
                            <strong>
                              {this.state.phonePreview.countryCode}
                            </strong>{" "}
                            · country{" "}
                            <strong>
                              {this.state.phonePreview.country || "—"}
                            </strong>{" "}
                            · iso{" "}
                            <strong>
                              {this.state.phonePreview.isoCode || "—"}
                            </strong>
                          </Typography>
                        </Box>
                      ) : null}

                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={this.state.setTwoFAFromContact}
                            onChange={(e) =>
                              this.setState({
                                setTwoFAFromContact: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Also set this as the 2FA number"
                      />

                      <Box>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={
                            this.state.phoneUpdateInProgress ||
                            !this.state.phoneInput?.trim()
                          }
                          onClick={() =>
                            this.updateAgentPhone({
                              contact: this.state.phoneInput,
                              setTwoFAFromContact:
                                this.state.setTwoFAFromContact,
                            })
                          }
                        >
                          Update Phone
                        </Button>
                      </Box>
                    </Box>

                    <Box
                      style={{
                        padding: "16px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                      }}
                    >
                      <Typography
                        variant="body2"
                        gutterBottom
                        style={{ fontWeight: 600 }}
                      >
                        2FA number (with country code)
                      </Typography>
                      <TextField
                        style={{
                          width: "100%",
                          border: "1px solid ",
                          background: "white",
                        }}
                        name="twoFAInput"
                        placeholder="+971 52 516 8261"
                        value={this.state.twoFAInput}
                        onChange={this.handleInput}
                      />
                      <Box style={{ marginTop: "12px" }}>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={
                            this.state.phoneUpdateInProgress ||
                            !this.state.twoFAInput?.trim()
                          }
                          onClick={() =>
                            this.updateAgentPhone({
                              twoFANumber: this.state.twoFAInput,
                            })
                          }
                        >
                          Update 2FA Number
                        </Button>
                      </Box>
                      <Typography
                        variant="body2"
                        style={{ color: "gray", marginTop: "8px" }}
                      >
                        Changing the 2FA number logs this account out of all
                        active sessions.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </div>
          )}

        {clientsWritePermission && this.canManageAutomationAccount() && (
          <div className={classes.root}>
            <Grid container justify="center">
              <Grid item xs={12} md={8}>
                <Box py={2} px={2} my={2} className={classes.container}>
                  <Typography
                    variant="body1"
                    gutterBottom
                    style={{
                      fontWeight: 900,
                      fontSize: "18px",
                      marginBottom: "16px",
                    }}
                  >
                    Automation Account
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={!!this.state.automationAccount}
                        disabled={
                          this.state.automationAccount === null ||
                          this.state.automationAccountUpdating
                        }
                        onChange={(e) =>
                          this.toggleAutomationAccount(e.target.checked)
                        }
                      />
                    }
                    label={this.state.automationAccount ? "Enabled" : "Disabled"}
                  />
                  <Typography
                    variant="body2"
                    style={{ color: "gray", marginTop: "8px" }}
                  >
                    For QA automation only. When enabled, the master OTP logs
                    in this account's owner, managers and agents on every
                    environment, and 2FA OTPs are not sent.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </div>
        )}

        <div className={classes.root}>
          <Grid container justify="center">
            <Grid item xs={12} md={8}>
              <Box py={2} px={2} my={2} className={classes.container}>
                <Typography
                  variant="body1"
                  gutterBottom
                  style={{
                    fontWeight: 900,
                    fontSize: "18px",
                    marginBottom: "24px",
                  }}
                >
                  OTP Management
                </Typography>

                {this?.state?.getOtpResponseData?.map((item, index) => {
                    const labels = [
                      "Login 2FA OTP",
                      "Password Reset OTP",
                      "In platform",
                    ];
                    const keys = ["login", "passwordReset", "platform"];

                    return (
                      <Box
                        key={keys[index]}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: "10px",
                          backgroundColor: "white",
                          padding: "20px",
                          borderRadius: "8px",
                        }}
                      >
                        <Typography
                          style={{ fontWeight: "600", width: "200px" }}
                        >
                          {labels[index]}
                        </Typography>
                        <Typography
                          style={{
                            width: "100px",
                            textAlign: "center",
                          }}
                        >
                          OTP:{" "}
                          <strong
                            style={{
                              fontWeight: 900,
                              fontSize: "16px",
                            }}
                          >
                            {item?.otp || "No OTP"}
                          </strong>
                        </Typography>

                        {clientsWritePermission && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => this.resetButtonHandler(keys[index])}
                          >
                            Reset
                          </Button>
                        )}
                      </Box>
                    );
                  })}
              </Box>
            </Grid>
          </Grid>
        </div>

        <div className={classes.root}>
          <Grid container justify="center">
            <Grid item xs={12} md={8}>
              <Box py={2} px={2} my={2} className={classes.container}>
                <Typography
                  variant="body1"
                  gutterBottom
                  style={{
                    fontWeight: 900,
                    fontSize: "18px",
                    marginBottom: "24px",
                  }}
                >
                  Assistants
                </Typography>

                {agent?.assistantDetails?.length ? (
                  <Box
                    style={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      overflowX: "auto",
                    }}
                  >
                    <table className={classes.assistantsTable}>
                      <thead>
                        <tr>
                          <th>Assistant ID</th>
                          <th>WABA Status</th>
                          <th>Plan Name</th>
                          <th>Plan Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agent.assistantDetails.map((item) => (
                          <tr key={item.assistantId}>
                            <td>
                              <Tooltip
                                title={
                                  this.state.copiedAssistantId ===
                                  item.assistantId
                                    ? "Copied!"
                                    : "Click to copy"
                                }
                              >
                                <span
                                  onClick={() =>
                                    this.handleCopyAssistantId(item.assistantId)
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  {item.assistantId}
                                </span>
                              </Tooltip>
                            </td>
                            <td>{item.wabaStatus || "-"}</td>
                            <td>{item.planName || "-"}</td>
                            <td>{item.planStatus || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                ) : (
                  <Typography
                    style={{
                      backgroundColor: "white",
                      padding: "20px",
                      borderRadius: "8px",
                    }}
                  >
                    No assistants found.
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </div>

        <Snackbar
          open={this.state.alert}
          autoHideDuration={4000}
          onClose={this.closeSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={this.closeSnackbar}
            severity={this.state.alertSeverity}
          >
            {this.state.alertMsg}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

const styles = (theme) => ({
  root: {
    // width: "100%",
    paddingTop: 20,
    overflowX: "hidden",
  },
  dialogContainer: {
    overflowX: "hidden",
    height: "100%",
    position: "relative",
    color: "black",
  },
  dialogTopbar: {
    position: "sticky",
    top: 0,
    left: 0,
    background: "white",
    zIndex: 100,
  },
  container: {
    background: "#EBF5F3",
    borderRadius: "15px",
  },
  disabledContainer: {
    background: "#dcdcdc",
    borderRadius: "15px",
  },
  textField: {
    width: 200,
  },
  assistantsTable: {
    width: "100%",
    borderCollapse: "collapse",
    "& th, & td": {
      textAlign: "left",
      padding: "12px 16px",
      borderBottom: "1px solid #eee",
      fontSize: "14px",
    },
    "& th": {
      fontWeight: 700,
      color: "gray",
      backgroundColor: "#fafafa",
    },
  },
});

const connectedClients = connect((state) => ({
  agent: state.login.user,
  permissions: state.login.permissions,
}))(Clients);

export default withStyles(styles)(connectedClients);
